import { useEffect, useRef, useState } from "react";
import { loadMapLibre } from "./mapLoader";
import { categoryMeta, glyphSvg, HOTEL_GLYPH } from "./categories";
import localization from "../../../../../../assets/localization";

export type MapPoint = {
  name: string;
  lat: number;
  lng: number;
  category?: string | null;
  order?: number | null; // set in itinerary mode: 1-based stop number
  rating?: number | null; // TripAdvisor rating, top-N enriched only
};

export type MapHotel = {
  name: string;
  lat: number;
  lng: number;
} | null;

interface ConciergeMapProps {
  hotel: MapHotel;
  points: MapPoint[];
  mode: "map" | "itinerary";
  transport?: "foot" | "bike" | "car";
  selectedIndex: number;
  onSelect: (index: number) => void;
  language: string;
}

// CARTO's Voyager look, on OpenFreeMap's data: see scripts/fetch-map-assets.mjs.
// The OpenStreetMap / OpenMapTiles credits come from the tile source itself.
const STYLE_ATTRIBUTION =
  '<a href="https://carto.com/attributions" target="_blank">&copy; CARTO</a>';

// MapLibre tiles are 512px where Leaflet's were 256, so the same view sits one
// zoom level lower. The numbers below stay the ones the framing was tuned on.
const zoom = (leafletZoom: number) => leafletZoom - 1;

// Tile errors before any tile has arrived mean the tile server is down: the
// guest gets the plain embed instead of an empty canvas.
const MAX_TILE_ERRORS = 3;
const LOAD_TIMEOUT_MS = 15000;

const SVG_NS = "http://www.w3.org/2000/svg";

// Real-road routing via the public FOSSGIS OSRM instances (OSM data, no key).
// Any failure falls back to the straight dashed legs already on the map.
const OSRM_PROFILES: Record<string, [string, string]> = {
  foot: ["routed-foot", "foot"],
  bike: ["routed-bike", "bike"],
  car: ["routed-car", "driving"],
};

const fetchOsrmRoute = async (
  waypoints: number[][],
  transport: string
): Promise<number[][] | null> => {
  const [service, profile] = OSRM_PROFILES[transport] ?? OSRM_PROFILES.foot;
  const coords = waypoints.map(([lat, lng]) => `${lng},${lat}`).join(";");
  const url = `https://routing.openstreetmap.de/${service}/route/v1/${profile}/${coords}?overview=full&geometries=geojson&steps=false`;
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 6000);
    const res = await fetch(url, { signal: controller.signal });
    clearTimeout(timer);
    if (!res.ok) return null;
    const data = await res.json();
    const line = data?.routes?.[0]?.geometry?.coordinates;
    if (!Array.isArray(line) || line.length < 2) return null;
    return line.map(([lng, lat]: number[]) => [lat, lng]);
  } catch {
    return null;
  }
};

const hotelIconHtml = (primary: string) =>
  `<div class="cmap-hotel">
     <span class="cmap-hotel-pulse" style="background:${primary}"></span>
     <span class="cmap-hotel-core" style="background:${primary}">${glyphSvg(
    HOTEL_GLYPH,
    16
  )}</span>
   </div>`;

const poiIconHtml = (point: MapPoint, index: number, primary: string) => {
  const meta = categoryMeta(point.category);
  const inner =
    point.order != null
      ? `<span class="cmap-pin-number">${point.order}</span>`
      : glyphSvg(meta.glyph);
  const color = point.order != null ? primary : meta.color;
  return `<div class="cmap-pin" style="--pin-color:${color};animation-delay:${
    120 + index * 70
  }ms">
     <span class="cmap-pin-head">${inner}</span>
     <span class="cmap-pin-tail"></span>
   </div>`;
};

export default function ConciergeMap({
  hotel,
  points,
  mode,
  transport = "foot",
  selectedIndex,
  onSelect,
  language,
}: ConciergeMapProps) {
  const t = localization.get(language);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  const markersRef = useRef<HTMLElement[]>([]);
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  const [failed, setFailed] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let disposed = false;
    let observer: ResizeObserver | null = null;
    let loadTimer: ReturnType<typeof setTimeout> | undefined;

    const dispose = () => {
      clearTimeout(loadTimer);
      observer?.disconnect();
      observer = null;
      markersRef.current = [];
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };

    const fail = () => {
      if (disposed) return;
      dispose();
      setFailed(true);
    };

    // Pass the container: it tells the loader which root (document or the
    // host page's shadow root) MapLibre's stylesheet has to be added to.
    loadMapLibre(containerRef.current)
      .then(({ maplibregl, style }) => {
        const container = containerRef.current;
        if (disposed || !container) return;

        const primary =
          getComputedStyle(container)
            .getPropertyValue("--primary-color")
            .trim() || "#7c3aed";

        const lngLats: [number, number][] = [
          ...(hotel ? [[hotel.lng, hotel.lat] as [number, number]] : []),
          ...points.map((p) => [p.lng, p.lat] as [number, number]),
        ];
        const initialView =
          lngLats.length > 1
            ? {
                bounds: lngLats.reduce(
                  (bounds, lngLat) => bounds.extend(lngLat),
                  new maplibregl.LngLatBounds(lngLats[0], lngLats[0])
                ),
                fitBoundsOptions: {
                  padding: { top: 56, left: 46, right: 46, bottom: 150 },
                },
              }
            : { center: lngLats[0] ?? [0, 0], zoom: zoom(15) };

        let map: any;
        try {
          map = new maplibregl.Map({
            container,
            style,
            ...initialView,
            maxZoom: zoom(19),
            attributionControl: false,
            dragRotate: false,
            pitchWithRotate: false,
            touchPitch: false,
          });
        } catch {
          // No WebGL on this device.
          fail();
          return;
        }
        mapRef.current = map;
        map.touchZoomRotate.disableRotation();
        map.keyboard.disableRotation();
        map.addControl(
          new maplibregl.AttributionControl({
            compact: false,
            customAttribution: STYLE_ATTRIBUTION,
          }),
          "bottom-right"
        );

        // Until the first tile arrives, a failing source means the tile server
        // is down: the TileJSON itself failing is final, single tiles get a
        // few tries. Once the map has drawn, a stray tile error changes nothing.
        let tilesArrived = false;
        let tileErrors = 0;
        map.on("data", (event: any) => {
          if (event?.sourceId === "carto" && event.tile) tilesArrived = true;
        });
        map.on("error", (event: any) => {
          if (tilesArrived || event?.sourceId !== "carto") return;
          if (!event.tile || ++tileErrors >= MAX_TILE_ERRORS) fail();
        });
        loadTimer = setTimeout(fail, LOAD_TIMEOUT_MS);
        map.once("load", () => {
          clearTimeout(loadTimer);
          if (!disposed) setReady(true);
        });

        // One hover label per marker, shown on hover and on tap.
        let openTooltip: any = null;
        const bindTooltip = (
          el: HTMLElement,
          lngLat: [number, number],
          html: string,
          offsetY: number
        ) => {
          const popup = new maplibregl.Popup({
            closeButton: false,
            closeOnClick: false,
            anchor: "bottom",
            offset: [0, offsetY],
            maxWidth: "none",
            className: "cmap-tooltip",
          })
            .setLngLat(lngLat)
            .setHTML(html);
          const show = () => {
            if (openTooltip && openTooltip !== popup) openTooltip.remove();
            openTooltip = popup;
            popup.addTo(map);
          };
          el.addEventListener("mouseenter", show);
          el.addEventListener("click", show);
          el.addEventListener("mouseleave", () => popup.remove());
        };

        // Route first so pins render above it. Starts as straight legs,
        // upgraded in place to a real OSRM path when the fetch lands. Drawn as
        // an SVG over the canvas, projected on every move: the marching-dots
        // animation is a CSS dash offset, which a WebGL line cannot run.
        if (mode === "itinerary" && points.length) {
          let path = lngLats.map(([lng, lat]) => [lat, lng]);
          const svg = document.createElementNS(SVG_NS, "svg");
          svg.setAttribute("class", "cmap-route-layer");
          const makeLine = (attrs: Record<string, string>) => {
            const line = document.createElementNS(SVG_NS, "path");
            Object.entries({
              fill: "none",
              "stroke-linecap": "round",
              "stroke-linejoin": "round",
              ...attrs,
            }).forEach(([key, value]) => line.setAttribute(key, value));
            svg.appendChild(line);
            return line;
          };
          const under = makeLine({
            stroke: "#ffffff",
            "stroke-width": "7",
            "stroke-opacity": "0.85",
          });
          const over = makeLine({
            stroke: primary,
            "stroke-width": "3.5",
            "stroke-dasharray": "1 9",
            class: "cmap-route",
          });
          map.getCanvasContainer().appendChild(svg);

          const draw = () => {
            const d = path
              .map(([lat, lng], i) => {
                const p = map.project([lng, lat]);
                return `${i ? "L" : "M"}${p.x.toFixed(1)} ${p.y.toFixed(1)}`;
              })
              .join("");
            under.setAttribute("d", d);
            over.setAttribute("d", d);
          };
          draw();
          map.on("move", draw);
          map.on("resize", draw);

          fetchOsrmRoute(path, transport).then((route) => {
            if (disposed || !route || !mapRef.current) return;
            path = route;
            draw();
          });
        }

        if (hotel) {
          const hotelLngLat: [number, number] = [hotel.lng, hotel.lat];
          const el = document.createElement("div");
          el.className = "cmap-marker hotel";
          el.style.width = "46px";
          el.style.height = "46px";
          el.innerHTML = hotelIconHtml(primary);
          new maplibregl.Marker({ element: el, anchor: "center" })
            .setLngLat(hotelLngLat)
            .addTo(map);
          bindTooltip(el, hotelLngLat, hotel.name || t.your_hotel, -18);
        }

        markersRef.current = points.map((point, index) => {
          const lngLat: [number, number] = [point.lng, point.lat];
          const el = document.createElement("div");
          el.className = "cmap-marker";
          el.style.width = "38px";
          el.style.height = "48px";
          el.innerHTML = poiIconHtml(point, index, primary);
          // The pin's tip sits 4px above the bottom of its box.
          new maplibregl.Marker({ element: el, anchor: "bottom", offset: [0, 4] })
            .setLngLat(lngLat)
            .addTo(map);
          bindTooltip(
            el,
            lngLat,
            point.rating != null
              ? `${point.name}<span class="cmap-tooltip-rating">★ ${point.rating}</span>`
              : point.name,
            -40
          );
          el.addEventListener("click", () => onSelectRef.current(index));
          return el;
        });

        observer = new ResizeObserver(() => map.resize());
        observer.observe(container);
      })
      .catch(fail);

    return () => {
      disposed = true;
      dispose();
    };
    // The template payload is immutable once rendered; the map is built once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Selection sync: highlight the active pin and glide to it. The first run
  // only highlights — the opening view stays on the fitBounds overview.
  const didInitRef = useRef(false);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    const isFirst = !didInitRef.current;
    didInitRef.current = true;
    markersRef.current.forEach((el, index) =>
      el.classList.toggle("selected", index === selectedIndex)
    );
    const point = points[selectedIndex];
    if (point && !isFirst) {
      map.flyTo({
        center: [point.lng, point.lat],
        zoom: Math.max(map.getZoom(), zoom(15)),
        duration: 800,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndex, ready]);

  if (failed) {
    // CDN or tile server unreachable, or no WebGL: fall back to the plain
    // keyless Google embed so the guest still gets a map, not a dead panel.
    const target = points[selectedIndex] ?? points[0] ?? hotel;
    if (!target) return null;
    const query = `${target.lat},${(target as any).lng}`;
    return (
      <iframe
        title={target.name || t.anchor_places}
        src={`https://maps.google.com/maps?q=${encodeURIComponent(
          query
        )}&z=15&hl=${language}&output=embed`}
        className="places-iframe"
        loading="lazy"
      />
    );
  }

  return (
    <div className="cmap-root">
      <div ref={containerRef} className="cmap-canvas" />
      {!ready && <div className="cmap-loading">{t.map_loading}</div>}
    </div>
  );
}
